import React, { useEffect, useRef, useState } from 'react';
import { categoryFields } from './panels/AddFormStep';
import { CaretDownIcon, CloseIcon, FilterIcon, TickIcon } from './icons';

const ANY = { value: '', label: 'Any' };

/**
 * The chosen values as /map/pins filters. Each key is the column name in
 * lower case, which is how Property::MAP_FILTERS names them.
 *
 * @returns {object} empty when nothing is chosen.
 */
export function categoryPinFilters(values) {
    const filters = {};

    categoryFields(values).forEach((field) => {
        if (values[field.key]) filters[field.key.toLowerCase()] = [values[field.key]];
    });

    return filters;
}

function chosenLabels(values) {
    return categoryFields(values)
        .filter((field) => values[field.key])
        .map((field) => {
            const option = field.options.filter((item) => item.value === values[field.key])[0];

            return option ? option.label.trim() : values[field.key];
        });
}

/** Why the tenant row is empty, when the chain has not reached it yet. */
function tenantHint(values) {
    if (values.CATEGORY === 'apartments') return null;

    return values.TYPE ? 'None for this type' : 'Pick Single- or Multi-Tenant first';
}

/**
 * The crawler settings' select (ls-select / ls-menu) in this view's dark
 * palette: a button showing the pick, and a list with a tick on it.
 */
function MenuSelect({ field, value, isOpen, onToggle, onPick }) {
    const listNode = useRef(null);
    const options = [ANY].concat(field.options);
    const picked = value || '';
    const chosen = options.filter((option) => option.value === picked)[0];

    // Open on the pick, so the keyboard starts where the eye does.
    useEffect(() => {
        if (!isOpen || !listNode.current) return;

        const on = listNode.current.querySelector('.ParcelMap-option--on') || listNode.current.firstChild;

        if (on) on.focus();
    }, [isOpen]);

    const onKeyDown = (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

        event.preventDefault();

        const sibling = event.key === 'ArrowDown' ? event.target.nextSibling : event.target.previousSibling;

        if (sibling) sibling.focus();
    };

    return (
        <div className={'ParcelMap-filter-field' + (isOpen ? ' ParcelMap-filter-field--open' : '')}>
            <span className="Form-label">{field.label}</span>
            <button
                type="button"
                className={'ParcelMap-select' + (picked ? ' ParcelMap-select--set' : '') + (isOpen ? ' ParcelMap-select--open' : '')}
                data-field={field.key}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={onToggle}
            >
                <span className="ParcelMap-select-value">{chosen ? chosen.label.trim() : picked}</span>
                <span className={'ParcelMap-layers-caret' + (isOpen ? ' ParcelMap-layers-caret--open' : '')}>
                    <CaretDownIcon />
                </span>
            </button>

            {isOpen && (
                <div className="ParcelMap-options" role="listbox" aria-label={field.label} ref={listNode} onKeyDown={onKeyDown}>
                    {options.map((option) => {
                        const on = option.value === picked;

                        return (
                            <button
                                key={option.value || 'any'}
                                type="button"
                                role="option"
                                aria-selected={on}
                                className={'ParcelMap-option' + (on ? ' ParcelMap-option--on' : '') + (option.value ? '' : ' ParcelMap-option--any')}
                                onClick={() => onPick(option.value)}
                            >
                                <span className="ParcelMap-option-name">{option.label.trim()}</span>
                                {on && <span className="ParcelMap-option-check"><TickIcon /></span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/**
 * Category, type and tenant subcategory, picked from the same chain the
 * property page offers; whatever matches lights up on the map as it is picked.
 */
export default function CategoryFilter({ values, onChange, onClear, onShowAll, matchCount, isLoading, error }) {
    const [isOpen, setIsOpen] = useState(false);
    const [openField, setOpenField] = useState(null);
    const rootNode = useRef(null);
    // Read by the key handler, which can run before the effect re-subscribes
    // with the list that was just opened.
    const openFieldNow = useRef(null);
    openFieldNow.current = openField;
    const fields = categoryFields(values);
    const labels = chosenLabels(values);
    const isActive = labels.length > 0;
    const hint = fields.length < 3 ? tenantHint(values) : null;

    useEffect(() => {
        if (!isOpen) return undefined;

        const onDown = (event) => {
            if (rootNode.current && !rootNode.current.contains(event.target)) {
                setIsOpen(false);
                setOpenField(null);
            } else if (!event.target.closest('.ParcelMap-filter-field--open')) {
                setOpenField(null);
            }
        };
        // Capture, and stopped here, so the map's own Escape does not also
        // close the side panel behind the menu. An open list goes first.
        const onKey = (event) => {
            if (event.key !== 'Escape') return;

            event.stopPropagation();

            if (openFieldNow.current) {
                setOpenField(null);
            } else {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', onDown, true);
        document.addEventListener('keydown', onKey, true);

        return () => {
            document.removeEventListener('mousedown', onDown, true);
            document.removeEventListener('keydown', onKey, true);
        };
    }, [isOpen]);

    const toggle = () => {
        setIsOpen(!isOpen);
        setOpenField(null);
    };

    return (
        <div className="ParcelMap-filter" ref={rootNode}>
            <div className={'ParcelMap-filter-trigger' + (isActive ? ' ParcelMap-filter-trigger--active' : '') + (isOpen ? ' ParcelMap-filter-trigger--open' : '')}>
                <button
                    type="button"
                    className="ParcelMap-filter-button"
                    onClick={toggle}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    title={isActive ? labels.join(' · ') : 'Highlight properties by category'}
                >
                    {isActive ? <span className="ParcelMap-filter-dot" /> : <FilterIcon />}
                    <span className="ParcelMap-filter-label">{isActive ? labels.join(' · ') : 'Category'}</span>
                    {isActive && (
                        <span className="ParcelMap-filter-count">
                            {isLoading ? <span className="ParcelMap-filter-spinner" /> : matchCountText(matchCount, error)}
                        </span>
                    )}
                    {!isActive && (
                        <span className={'ParcelMap-layers-caret' + (isOpen ? ' ParcelMap-layers-caret--open' : '')}>
                            <CaretDownIcon />
                        </span>
                    )}
                </button>
                {isActive && (
                    <button type="button" className="ParcelMap-filter-clear" onClick={onClear} aria-label="Clear category filter" title="Clear">
                        <CloseIcon />
                    </button>
                )}
            </div>

            {isOpen && (
                <div className="ParcelMap-filter-menu" role="dialog" aria-label="Highlight by category">
                    <div className="ParcelMap-layers-heading">Highlight properties</div>

                    <div className="ParcelMap-filter-fields">
                        {fields.map((field) => (
                            <MenuSelect
                                key={field.key}
                                field={field}
                                value={values[field.key]}
                                isOpen={openField === field.key}
                                onToggle={() => setOpenField(openField === field.key ? null : field.key)}
                                onPick={(value) => {
                                    setOpenField(null);
                                    onChange(field.key, value);
                                }}
                            />
                        ))}

                        {hint && (
                            <div className="ParcelMap-filter-field">
                                <span className="Form-label">Tenant Subcategory</span>
                                <div className="ParcelMap-filter-placeholder">{hint}</div>
                            </div>
                        )}
                    </div>

                    <div className="ParcelMap-filter-footer">
                        <span className={'ParcelMap-filter-result' + (error ? ' ParcelMap-filter-result--error' : '')}>
                            {resultText(isActive, matchCount, isLoading, error)}
                        </span>
                        {isActive && (
                            <span className="ParcelMap-filter-actions">
                                {matchCount > 0 && !isLoading && (
                                    <button type="button" className="Btn Btn--sm" onClick={onShowAll} title="Fit the map to every match">
                                        Show all
                                    </button>
                                )}
                                <button type="button" className="Btn Btn--sm" onClick={onClear}>Clear</button>
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function matchCountText(matchCount, error) {
    if (error) return '!';

    return matchCount === null ? '' : matchCount.toLocaleString();
}

function resultText(isActive, matchCount, isLoading, error) {
    if (!isActive) return 'Pick any field to highlight what matches.';
    if (error) return 'Could not load matches. Change a field to retry.';
    if (isLoading || matchCount === null) return 'Finding matches…';
    if (matchCount === 0) return 'No properties match.';

    return matchCount.toLocaleString() + (matchCount === 1 ? ' match' : ' matches');
}
